"use client";

import React, { useRef, useState } from "react";
import { motion } from "motion/react";
import { Button } from "@heroui/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowRight01Icon, SparklesIcon } from "@hugeicons/core-free-icons";

// Magnetic effect wrapper matching Home page styling
function Magnetic({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const x = clientX - (left + width / 2);
    const y = clientY - (top + height / 2);
    setPosition({ x: x * 0.35, y: y * 0.35 });
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      animate={{ x: position.x, y: position.y }}
      transition={{ type: "spring", stiffness: 150, damping: 15, mass: 0.1 }}
      className="w-full sm:w-auto flex justify-center"
    >
      {children}
    </motion.div>
  );
}

export default function CTA() {
  return (
    <div className="py-24 relative px-4 overflow-hidden bg-neutral-50 dark:bg-black transition-colors duration-500 flex flex-col justify-center items-center">
      {/* Decorative colored glow spheres */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-blue-500/10 to-indigo-500/10 dark:from-blue-500/[0.02] dark:to-indigo-500/[0.02] blur-[130px] pointer-events-none" />

      <div className="max-w-4xl w-full py-16 px-6 sm:px-12 relative z-10 border border-neutral-200/50 dark:border-neutral-800/40 bg-white/70 dark:bg-neutral-900/10 backdrop-blur-2xl rounded-4xl shadow-xl flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="w-12 h-12 rounded-full flex items-center justify-center bg-blue-500/10 text-blue-600 dark:text-blue-400 mb-6"
        >
          <HugeiconsIcon icon={SparklesIcon} className="w-6 h-6 animate-pulse" size={24} />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-3xl font-bold text-slate-900 dark:text-white sm:text-5xl leading-tight font-recoleta text-center tracking-tight"
        >
          Ready to scale your
          <span className="block mt-2 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
            digital experience?
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-6 text-lg sm:text-xl text-neutral-600 dark:text-neutral-400 font-manjari font-light leading-relaxed max-w-xl text-center"
        >
          Let&#39;s build a custom tech infrastructure tailored exactly to your brand&#39;s goals. 
          Schedule a strategic sandbox session today.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.5,
            delay: 0.3,
            type: "spring",
            stiffness: 120,
          }}
          className="mt-10 w-full flex justify-center"
        >
          <Magnetic>
            <a href="/contact" className="inline-block outline-none w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto px-8 py-6 rounded-full font-bold text-white bg-gradient-to-r from-blue-600 via-indigo-650 to-purple-650 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-indigo-500/20 dark:shadow-none transition-all duration-300 font-outfit text-base cursor-pointer flex items-center justify-center gap-2 border-0 outline-none"
              >
                Start a Project
                <HugeiconsIcon icon={ArrowRight01Icon} className="w-5 h-5" size={20} />
              </Button>
            </a>
          </Magnetic>
        </motion.div>
      </div>
    </div>
  );
}
